import React, {useMemo, useState} from 'react';
import {Alert, Button, Card, Col, Grid, List, message, Row, Select, Space, Table, Typography} from 'antd';
import {ReloadOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {EmptyState, ErrorState, LoadingState, PageHeader} from '@dowty/ui';
import {useOutbox} from '@dowty/offline';
import dayjs from 'dayjs';
import {Activity} from '../generated/activity_pb';
import type {Sporttypes} from '../generated/activity_pb';
import {ActivitatorClient} from '../generated/activity_grpc_web_pb';
import {UserData} from '../generated/user_pb';
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS} from '../grpcConfig';
import AddActivityModal from '../Components/AddActivityModal';
import {enqueueActivity, NO_SPORTTYPES, outbox, toActivity, useActivities, useSporttypes, userOutbox} from '../offline';
import {text} from '../utils/language';
import {paginationItemRender} from '../utils/pagination';

const {Text} = Typography;
type HistoryRow = {
    key: string;
    date: string;
    dateValue: string;
    timestamp: number;
    sport: string;
    sportName: string;
    details: string;
    parameterOne: number;
    parameterTwo: number;
    pending?: string;
    outboxId?: string;
};
type ActivityChange = {sport: string | null; date: string | number; parameterOne: number; parameterTwo: number; id: number | string};

async function saveActivity(data: ActivityChange | {templateName: string; activities: Activity[]}, sportTypes: Sporttypes | undefined, editId: number | undefined, closeEditor: () => void, reload: () => void) {
    if ('activities' in data) return false;
    if (!sportTypes || editId === undefined) {
        message.error(text('Aktivität konnte nicht gespeichert werden.', 'Unable to save activity'));
        return false;
    }
    await enqueueActivity(sportTypes, data, editId);
    closeEditor();
    reload();
    return true;
}

function deleteActivity(data: ActivityChange, sportTypes: Sporttypes | undefined, client: ActivitatorClient, setDeletedId: (id: number | null) => void, closeEditor: () => void, reload: () => void): Promise<boolean> {
    const userString = localStorage.getItem('user');
    const sport = sportTypes?.getSportypeList().find(item => item.getSporttypename() === data.sport);
    if (!userString || !sport) { message.error(text('Aktivität konnte nicht gelöscht werden.', 'Could not delete activity.')); return Promise.resolve(false); }
    const activity = new Activity();
    activity.setUser(UserData.deserializeBinary(Uint8Array.from(atob(userString), character => character.codePointAt(0)!)));
    activity.setSportype(sport);
    activity.setId(Number(data.id));
    activity.setParameterone(data.parameterOne);
    activity.setParametertwo(data.parameterTwo);
    setDeletedId(Number(data.id));
    return new Promise<boolean>(resolve => client.deleteActivity(activity, undefined, (error, response) => {
        if (error || !response) {
            setDeletedId(null);
            message.error(text('Aktivität konnte nicht gelöscht werden.', 'Could not delete activity.'));
            resolve(false);
        } else { closeEditor(); reload(); resolve(true); }
    }));
}

const sampleRows: HistoryRow[] = [
    {key: '1', date: 'May 1, 2025', dateValue: '01-05-2025', timestamp: new Date('2025-05-01').getTime(), sport: 'Running', sportName: 'Running', details: 'Amount: 50 · Parameter 2: 1', parameterOne: 50, parameterTwo: 1},
    {key: '2', date: 'Apr 28, 2025', dateValue: '28-04-2025', timestamp: new Date('2025-04-28').getTime(), sport: 'Cycling', sportName: 'Cycling', details: 'Amount: 30', parameterOne: 30, parameterTwo: 0},
    {key: '3', date: 'Apr 22, 2025', dateValue: '22-04-2025', timestamp: new Date('2025-04-22').getTime(), sport: 'Swimming', sportName: 'Swimming', details: 'Amount: 100 · Parameter 2: 5', parameterOne: 100, parameterTwo: 5},
    {key: '4', date: 'Apr 20, 2025', dateValue: '20-04-2025', timestamp: new Date('2025-04-20').getTime(), sport: 'Running', sportName: 'Running', details: 'Amount: 40 · Parameter 2: 10', parameterOne: 40, parameterTwo: 10},
];

const toRow = (activity: Activity): HistoryRow => {
    const sport = activity.getSportype();
    const details = [
        `${sport?.getParameterone() || (text('Menge', 'Amount'))}: ${activity.getParameterone()}`,
        (sport?.getParametertwo() || activity.getParametertwo() !== 0) && `${sport?.getParametertwo() || 'Parameter 2'}: ${activity.getParametertwo()}`,
    ].filter(Boolean).join(' · ');
    const date = activity.getTsinsert()?.toDate();
    const dateFormat = text('DD.MM.YYYY', 'MMM D, YYYY');
    return {
        key: String(activity.getId()),
        date: date ? dayjs(date).format(dateFormat) : '—',
        dateValue: date ? dayjs(date).format('DD-MM-YYYY') : '',
        timestamp: date?.getTime() ?? 0,
        sport: sport?.getSporttypename() ?? (text('Unbekannt', 'Unknown')),
        sportName: sport?.getSporttypename() ?? (text('Unbekannt', 'Unknown')),
        details,
        parameterOne: activity.getParameterone(),
        parameterTwo: activity.getParametertwo(),
    };
};

function historyContent(read: ReturnType<typeof useActivities>, rows: HistoryRow[], isMobile: boolean, editButton: (row: HistoryRow) => React.ReactNode) {
    const columns = [
        {title: text('Datum', 'Date'), dataIndex: 'date', key: 'date'},
        {title: text('Aktivität', 'Activity'), dataIndex: 'sport', key: 'sport'},
        {title: text('Einheit', 'Session details'), dataIndex: 'details', key: 'details'},
        {title: text('Aktion', 'Action'), key: 'action', render: (_: unknown, row: HistoryRow) => editButton(row)},
    ];
    if (read.loading) return <LoadingState variant="list" label={text('Aktivitäten werden geladen', 'Loading activities')} />;
    if (read.error && localStorage.getItem('user')) return <ErrorState message={text('Aktivitäten konnten nicht geladen werden.', 'Could not load activities.')} retry={read.reload} retryLabel={text('Erneut versuchen', 'Retry')} />;
    if (rows.length === 0) return <EmptyState title={text('Keine Aktivitäten', 'No activities')} description={text('Passe den Filter an oder trage eine Einheit ein.', 'Change the filter or log a session.')} />;
    if (!isMobile) return <Table dataSource={rows} columns={columns} pagination={{pageSize: 10, itemRender: paginationItemRender}} rowKey={row => row.outboxId ?? row.key} scroll={{x: true}} />;
    return <List dataSource={rows} pagination={{pageSize: 10, itemRender: paginationItemRender}} renderItem={row => <List.Item key={row.outboxId ?? row.key}>
        <Space direction="vertical" size={0}>
            <Text strong>{row.sport}</Text>
            <Text>{row.details}</Text>
            <Text type="secondary">{row.date}</Text>
        </Space>
        {editButton(row)}
    </List.Item>} />;
}

const ActivityHistory: React.FC<{onAddActivityClick: () => void}> = ({onAddActivityClick}) => {
    const isMobile = !Grid.useBreakpoint().md;
    const activitiesRead = useActivities();
    const sportTypes = useSporttypes().data;
    const [selectedSport, setSelectedSport] = useState('All');
    const [deletedActivityId, setDeletedActivityId] = useState<number | null>(null);
    const [editData, setEditData] = useState<{id: number; date: string; sport: string; parameterOne: number; parameterTwo: number} | null>(null);
    const activityClient = useMemo(() => new ActivitatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS), []);
    const pendingEntries = useOutbox(userOutbox).entries;
    const serverRows = activitiesRead.data?.getActivitiesList().map(toRow) ?? (localStorage.getItem('user') ? [] : sampleRows);
    const rows = serverRows.map(row => {
        const entry = pendingEntries.find(item => item.payload.id === Number(row.key));
        return entry ? {...toRow(toActivity(entry.payload)), pending: entry.status === 'failed' ? 'rejected' : 'queued', outboxId: entry.id} : row;
    }).concat(pendingEntries.filter(entry => entry.payload.id === undefined || !serverRows.some(row => Number(row.key) === entry.payload.id)).map(entry => ({
        ...toRow(toActivity(entry.payload)),
        pending: entry.status === 'failed' ? 'rejected' : 'queued',
        outboxId: entry.id,
    }))).filter(row => Number(row.key) !== deletedActivityId).map(row => {
        if (!row.pending) return row;
        let pendingLabel: string;
        if (row.pending === 'queued') pendingLabel = text('wartet', 'queued');
        else pendingLabel = text('nicht gespeichert', 'not saved');
        return {...row, sport: `${row.sport} (${pendingLabel})`};
    });
    const failedIds = rows.filter(row => row.pending === 'rejected').map(row => row.outboxId!);
    const filteredRows = useMemo(() => rows
        .filter(row => selectedSport === 'All' || row.sportName === selectedSport)
        .sort((a, b) => b.timestamp - a.timestamp), [rows, selectedSport]);
    const closeEditor = () => setEditData(null);
    const editButton = (row: HistoryRow) => <Button type="link" disabled={Boolean(row.pending)} aria-label={`${text('Aktivität bearbeiten', 'Edit activity')}: ${row.sport}, ${row.date}`} onClick={() => setEditData({id: Number(row.key), date: row.dateValue, sport: row.sportName, parameterOne: row.parameterOne, parameterTwo: row.parameterTwo})}>{text('Bearbeiten', 'Edit')}</Button>;
    return <div className="sf-activity-history-page">
        <PageHeader title={text('Aktivitätsverlauf', 'Activity history')} subtitle={text('Alle deine Trainingseinheiten', 'All your training sessions')} extra={<Button aria-label={text('Aktualisieren', 'Reload activity history')} icon={<ReloadOutlined />} onClick={activitiesRead.reload} />} />
        <Card>
            <Row gutter={[12, 12]} justify="space-between" align="middle">
                <Col xs={24} sm={12}>
                    <Select aria-label={text('Nach Aktivität filtern', 'Filter by activity')} value={selectedSport} onChange={setSelectedSport} style={{width: '100%'}}>
                        <Select.Option value="All">{text('Alle Aktivitäten', 'All activities')}</Select.Option>
                        {sportTypes?.getSportypeList().map(sport => <Select.Option key={sport.getSporttypename()} value={sport.getSporttypename()}>{sport.getSporttypename()}</Select.Option>)}
                    </Select>
                </Col>
                <Col><Button type="primary" onClick={onAddActivityClick}>{text('Aktivität eintragen', 'Log activity')}</Button></Col>
            </Row>
            {failedIds.length > 0 && <Alert type="error" showIcon message={text(`${failedIds.length} Aktivitäten konnten nicht gespeichert werden.`, `${failedIds.length} activities could not be saved.`)} action={<Space wrap>
                <Button onClick={() => failedIds.forEach(id => void outbox.retry(id))}>{text('Erneut versuchen', 'Retry')}</Button>
                <Button onClick={() => failedIds.forEach(id => void outbox.remove(id))}>{text('Verwerfen', 'Discard')}</Button>
            </Space>} />}
            {historyContent(activitiesRead, filteredRows, isMobile, editButton)}
        </Card>
        <Link to="/dashboard">{text('Zurück zum Dashboard', 'Back to dashboard')}</Link>
        <AddActivityModal isOpen={editData !== null} onCancel={closeEditor} onOk={data => saveActivity(data, sportTypes, editData?.id, closeEditor, activitiesRead.reload)} onDelete={data => deleteActivity(data, sportTypes, activityClient, setDeletedActivityId, closeEditor, activitiesRead.reload)} mode="edit" initialData={editData} actualSportTypes={sportTypes ?? NO_SPORTTYPES} isTemplateCreation />
    </div>;
};

export default ActivityHistory;
